import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import moment, { Moment } from 'moment';
import { SessionStorageService } from '../../../core/services/session-storage/session-storage.service';
import { PiasLayoutComponent } from '../../../shared/components/pias-layout/pias-layout.component';
import { SnackbarService } from '../../../shared/services/snackbar/snackbar.service';
import { noWhitespace } from '../../../shared/validators/no-whitespace.validator';
import { ConfirmPopupComponent } from '../../agent-onboarding/new-rep-application/confirm-popup/confirm-popup.component';
import { HttpStatusCode } from 'axios';
import {
    AD_ACCEPT_ATTRIBUTE,
    AD_ACCEPTED_FORMATS,
    AD_MATERIAL_TYPES,
    AdAttachment,
    AdChannel,
    AdFileKind,
    AdMaterialType,
    MarketingAd,
    SaveAdPayload,
} from '../marketing-review-interfaces/marketing-review.interface';
import { MarketingReviewService } from '../marketing-review-service/marketing-review.service';
import { CreateAdModels } from './create-ad-models/create-ad-models';
import { CreateAdService } from './create-ad-service/create-ad-service';
import { environment } from '../../../../environments/environment';
import { MatCheckbox } from '@angular/material/checkbox';

/**
 * One attached file with everything the upload list and the preview need, resolved once when the
 * list changes rather than on every change-detection pass — `bypassSecurityTrustResourceUrl()`
 * hands back a new object each call, which would otherwise re-create the PDF iframe endlessly.
 */
interface Material {
    attachment: AdAttachment;
    kind: AdFileKind;
    /** Short badge, e.g. `PPT`. */
    format: string;
    icon: string;
    tone: string;
    /** `PPT · 1.8 MB` */
    meta: string;
    /** Sanitised URL for the PDF viewer; `null` for every other kind. */
    safeUrl: SafeResourceUrl | null;
}

/** DD/MM/YYYY datepicker convention shared with the onboarding and maintenance forms. */
export const MY_FORMATS = {
    parse: {
        dateInput: 'DD/MM/YYYY',
    },
    display: {
        dateInput: 'DD/MM/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'DD/MM/YYYY',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

/**
 * Compose — "Marketing Materials Application": name the campaign, pick the branch it is raised
 * for, say what kind of material it is, state who it goes to and how, attach the creative, then
 * submit for verification or park it as a draft.
 *
 * The form mirrors the paper Marketing Materials Application Form: campaign name, branch name,
 * type of marketing material, and the recipient / dissemination row. The form's "date of
 * application" is not collected — the record's own `createdOn` stamp serves for it.
 * `targetRecipients` and `modeOfDissemination` are free text exactly as the form has them, since
 * branches write things like "social media audience" and "email, hardcopy" rather than picking
 * from a list.
 *
 * ⚠️ `SaveAdPayload` still carries fields this form does not collect (channel, category, campaign
 * period, body copy, CTA, disclaimers). Documented defaults are sent for them so the stored
 * `MarketingAd` stays well-formed; anything downstream that reads them shows the default rather
 * than a submitter-supplied value.
 */
@Component({
    selector: 'app-create-ad',
    providers: [provideMomentDateAdapter(MY_FORMATS)],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        MatTooltipModule,
        PiasLayoutComponent,
        MatCheckbox
    ],
    templateUrl: './create-ad.component.html',
    styleUrl: './create-ad.component.scss'
})
export class CreateAdComponent implements OnInit {

    private router = inject(Router);
    private formBuilder = inject(FormBuilder);
    private matDialog = inject(MatDialog);
    private sanitizer = inject(DomSanitizer);
    private snackbarService = inject(SnackbarService);
    private sessionStorageService = inject(SessionStorageService);
    private marketingReviewService = inject(MarketingReviewService);

    private CreateAdService = inject(CreateAdService);

    protected readonly ACCEPTED_FILE_TYPES: string = AD_ACCEPT_ATTRIBUTE;

    /** Format chips listed under the upload card: video, GIF, PDF, PPT, JPEG, PNG, Word, Excel. */
    protected readonly ACCEPTED_FORMATS: ReadonlyArray<string> = AD_ACCEPTED_FORMATS.map((format) => format.label);

    /** Channel recorded for material raised here — the design's previews are all social. */
    private readonly DEFAULT_CHANNEL: AdChannel = 'Social Media';

    /** The 26 material types from the application form's abbreviation table, in its own order. */
    protected readonly MATERIAL_TYPES: ReadonlyArray<{ code: AdMaterialType; label: string }> = AD_MATERIAL_TYPES;

    protected readonly setSprinner = signal(false);

    protected adFg!: FormGroup;

    /** The attached material, with its resolved kind, badge and preview URL. */
    protected materials: Material[] = [];
    protected isDragOver: boolean = false;

    protected branches: string[] = [];

    /** True while the dissemination cell is collecting a note instead of a date. */
    protected useDisseminationNote: boolean = false;

    /** Set when editing an existing draft. */
    protected editingAd: MarketingAd | null = null;

    // ── Preview ─────────────────────────────────────────────────────────────────

    /**
     * Side-by-side preview panel, collapsed by default and toggled from the material field.
     *
     * It renders each file in the same viewer the review page uses, rather than in mock social
     * cards: the submitter should be checking what the reviewer will actually open, and half the
     * accepted formats (decks, documents, spreadsheets) never appear in a social feed at all.
     */
    protected previewOpen: boolean = false;

    /** Index into `materials` — which file the preview pane is showing. */
    protected activeMaterial: number = 0;

    protected zoom: number = 100;

    private readonly ZOOM_MIN: number = 50;
    private readonly ZOOM_MAX: number = 200;
    private readonly ZOOM_STEP: number = 25;
    protected editingApplicationId: number | null = null;
    pendingDocumentUploads: File[] = [];
    branchId: any[] = [];
    materialTypeId: any[] = [];
    protected branchName: string = '';
    marketingAnswers: { [key: number]: 'Yes' | 'No' | '' } = { 1: '', 2: '', 3: '' };


    ngOnInit(): void {
        console.log('token', this.sessionStorageService.getCurrentUserAuthToken());
        this.buildForm();
        this.loadBranches();
        this.loadAdForEditing();
        this.getBranches();
        this.getBranchName();
        this.getMarketingMaterialTypes();

    }
    async getBranchName(): Promise<void> {
        const repcode = this.currentRepCode;
        const response = await this.CreateAdService.getBranchname(repcode);
        this.branchName = response.branch;

        // console.log('Branch name:', response);
    }

    async getBranches(): Promise<void> {
        const res = await this.CreateAdService.getBranches();
        this.branchId = res.responsedata;
        // console.log(res.responsedata);
    }

    async getMarketingMaterialTypes(): Promise<void> {
        const res = await this.CreateAdService.getMaterialTypes();

        this.materialTypeId = res.responsedata;
        // console.log(res.responsedata);

    }

    protected async getMarketingReviewById(id: number): Promise<void> {
        this.setSprinner.set(true);

        const responseData = await this.CreateAdService
            .getMarketingReviewId(id)
            .then((response) => { return response })
            .catch((error) => { return error });

        this.setSprinner.set(false);

        if (responseData?.responsedata?.MarketingReviewApplication) {

            const application = responseData.responsedata.MarketingReviewApplication;

            this.editingApplicationId = application.marketingReviewApplicationId;
            const documents = responseData.responsedata.MarketingReviewApplication.documents;

            const attachments: AdAttachment[] = documents.map((doc: any) => ({
                id: doc.documentId,
                fileName: doc.documentName,
                fileType: doc.documentName?.split('.').pop()?.toLowerCase() ?? '',
                fileSizeKb: 0,
                url: doc.url ?? ''
            }));

            this.setMaterials(attachments);

            this.adFg.patchValue({
                title: application.marketingTitle ?? '',
                branch: Number(application.applicantBranchId),
                materialType: Number(application.marketingMaterialTypeId),
                targetRecipients: application.targetRecepients ?? '',
                expectedDisseminationDate:
                    application.expectedDateOfDissemination
                        ? moment(application.expectedDateOfDissemination)
                        : null,
                disseminationNote:
                    application.expectedCommentOfDissemination ?? '',
                modeOfDissemination:
                    application.modeOfDissemination ?? ''
            });

            this.useDisseminationNote = !!application.expectedCommentOfDissemination;

            this.applyDisseminationValidators();


        } else {

            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackbarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackbarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
    }
    async uploaddocument(file: File) {

        const formData = new FormData();

        formData.append('file', file);
        formData.append('applicationId', String(this.editingApplicationId));
        formData.append('documentTypeId', (this.adFg.get('materialType')?.value));


        const responseData = await this.CreateAdService
            .uploadDocuments(formData)
            .then((response) => { return JSON.parse(response); })
            .catch((error) => { return error; });
        console.log("document response", responseData)

        if (responseData.status === HttpStatusCode.Ok) {
            this.snackbarService.openSuccessSnackBar("document uploaded successfully!");
        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                this.snackbarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
        }
    }



    private buildForm(): void {
        this.adFg = this.formBuilder.group({
            title: ['', [Validators.required, noWhitespace]],
            branch: ['', [Validators.required]],
            materialType: ['', [Validators.required]],
            targetRecipients: ['', [Validators.required, noWhitespace]],
            // Holds a `Moment` once the datepicker has been used; `null` while empty.
            expectedDisseminationDate: [null as Moment | null, [Validators.required]],
            disseminationNote: [''],
            modeOfDissemination: ['', [Validators.required, noWhitespace]]
        });
    }

    /**
     * Swaps the dissemination cell between a date picker and a free-text note.
     *
     * Branches often cannot name a date — the paper form is routinely returned with "immediate
     * upon approval" written in that cell — so the note is a first-class alternative rather than a
     * checkbox that blanks the field. Exactly one of the two is filled, and only that one is
     * required, which is why the other is cleared here as well as validated.
     */
    protected toggleDisseminationNote(): void {
        this.useDisseminationNote = !this.useDisseminationNote;
        this.applyDisseminationValidators();
        this.adFg.markAsDirty();
    }

    private applyDisseminationValidators(): void {
        const dateControl = this.adFg.get('expectedDisseminationDate');
        const noteControl = this.adFg.get('disseminationNote');

        if (!dateControl || !noteControl) {
            return;
        }

        if (this.useDisseminationNote) {
            dateControl.setValue(null);
            dateControl.clearValidators();
            noteControl.setValidators([Validators.required, noWhitespace]);
        } else {
            noteControl.setValue('');
            noteControl.clearValidators();
            dateControl.setValidators([Validators.required]);
        }

        dateControl.updateValueAndValidity();
        noteControl.updateValueAndValidity();
    }

    private async loadBranches(): Promise<void> {
        await this.marketingReviewService.listBranches()
            .then((branches) => {
                this.branches = branches;
            })
            .catch((error) => {
                console.error('Failed to load the branch list:', error);
            });
    }

    /** Edit mode: prefer the object handed over in router state, fall back to fetching by id. */
    private async loadAdForEditing(): Promise<void> {
        const stateAd: MarketingAd | undefined = history.state?.ad;

        if (stateAd) {
            this.patchForm(stateAd);
            return;
        }

        // const idParam: string | null = new URLSearchParams(window.location.search).get('id');
        // if (!idParam) {
        //     return;
        // }
        const params = new URLSearchParams(window.location.search);

        const marketingReviewId =
            params.get('marketingReviewApplicationId');


        if (marketingReviewId) {
            await this.getMarketingReviewById(Number(marketingReviewId));
            return;
        }

        const idParam: string | null = params.get('id');

        if (!idParam) {
            return;
        }

        this.setSprinner.set(true);
        await this.marketingReviewService.getAdById(Number(idParam))
            .then((ad) => {
                // if (ad) {
                //     this.patchForm(ad);
                // }
            })
            .catch((error) => {
                console.error('Failed to load the advertisement for editing:', error);
            });
        this.setSprinner.set(false);

    }

    private patchForm(ad: MarketingAd): void {
        this.editingAd = ad;
        this.setMaterials(ad.attachments);

        // Which cell the draft used, then its validators, before the values land — switching
        // modes clears the control it is switching away from.
        this.useDisseminationNote = !!ad.disseminationNote;
        this.applyDisseminationValidators();

        this.adFg.patchValue({
            title: ad.title,
            // The form raises one branch; a legacy record raised for several opens on its first.
            branch: ad.branches[0] ?? '',
            materialType: ad.materialType ?? '',
            targetRecipients: ad.targetRecipients,
            expectedDisseminationDate: ad.expectedDisseminationDate
                ? moment(ad.expectedDisseminationDate, 'YYYY-MM-DD')
                : null,
            disseminationNote: ad.disseminationNote,
            modeOfDissemination: ad.modeOfDissemination
        });
    }

    /** The picked material type, for the closed select's trigger; `null` while nothing is picked. */
    protected get selectedMaterialType(): any {
        const id = this.adFg.get('materialType')?.value;
        return this.materialTypeId.find((type) => type.marketingMaterialId === id) ?? null;
    }
    protected get isPptRequired(): boolean {
        const EDM = this.selectedMaterialType?.materialKeyword === 'EDM';

        const PPT = this.materials.some(material => {
            const fileName = material.attachment?.fileName?.toLowerCase() ?? '';
            const fileType = material.attachment?.fileType?.toLowerCase() ?? '';

            return (
                ['ppt', 'pptx'].includes(fileType) ||
                fileName.endsWith('.ppt') ||
                fileName.endsWith('.pptx')
            );
        });

        return EDM && !PPT;
    }

    protected get pageTitle(): string {
        return 'Marketing Materials Application';
    }

    /** Earliest selectable dissemination date — material cannot go out before it is submitted. */
    protected readonly minDisseminationDate: Moment = moment().startOf('day');

    // ── Creative assets ─────────────────────────────────────────────────────────

    protected onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files?.length) {
            this.addFiles(Array.from(input.files));
        }
        // Reset so re-picking the same file still fires a change event.
        input.value = '';
    }

    protected onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = true;
    }

    protected onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;
    }

    protected onDrop(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;
        if (event.dataTransfer?.files?.length) {
            this.addFiles(Array.from(event.dataTransfer.files));
        }
    }

    /** Screens dropped and picked files on type, then on size, before uploading what is left. */
    private async addFiles(files: File[]): Promise<void> {
        const { accepted, wrongType, tooLarge } = this.marketingReviewService.screenAdFiles(files);


        if (wrongType.length) {
            this.snackbarService.openWarningSnackBar(
                `${this.nameList(wrongType)} ${wrongType.length === 1 ? 'is' : 'are'} not an accepted format and was skipped.`
            );
        }

        if (tooLarge.length) {
            this.snackbarService.openWarningSnackBar(
                `${this.nameList(tooLarge)} exceeds the ${this.marketingReviewService.MAX_FILE_SIZE_MB}MB limit `
                + `(${this.marketingReviewService.MAX_VIDEO_SIZE_MB}MB for video) and was skipped.`
            );
        }

        if (!accepted.length) {
            return;
        }

        this.setSprinner.set(true);
        if (this.editingApplicationId !== null) {
            await Promise.all(
                accepted.map(async (file) => {
                    await this.uploaddocument(file);
                })
            );
        } else {
            this.pendingDocumentUploads.push(...accepted);
        }
        await Promise.all(accepted.map((file) => this.marketingReviewService.uploadAdAsset(file)))
            .then((uploaded) => {
                this.setMaterials([...this.attachments, ...uploaded]);
                this.adFg.markAsDirty();
            })
            .catch((error) => {
                console.error('Failed to attach the creative files:', error);
                this.snackbarService.openFailureSnackBar('We couldn’t attach those files. Please try again');
            });
        this.setSprinner.set(false);
    }


    private nameList(files: File[]): string {
        return files.map((file) => file.name).join(', ');
    }

    async removeMaterial(document: any): Promise<void> {
        // this.setMaterials(
        //     this.attachments.filter((attachment) => attachment.id !== material.attachment.id)
        // );
        // this.adFg.markAsDirty();
        const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
            width: '500px',
            disableClose: true,
            data: {
                message: 'Do you want to remove the attachment?',
                hidecancel: false
            }
        });

        dialogRef.afterClosed().subscribe(async result => {
            if (result?.data !== true) {
                return;
            }

            const data = {
                documentId: document.attachment?.id ??
                    document.attachment?.documentId,
                marketingReviewApplicationId: this.editingApplicationId
            };

            const response = await this.CreateAdService
                .deletedocument(data)
                .then(response => JSON.parse(response))
                .catch(error => error);

            if (
                response?.status === HttpStatusCode.Ok ||
                response?.data?.status === HttpStatusCode.Ok ||
                response?.message?.toLowerCase()?.includes('success')
            ) {
                this.materials = this.materials.filter(
                    (item: any) =>
                        Number(item.documentId ?? item.id) !==
                        Number(data.documentId)
                );

                this.snackbarService.openSuccessSnackBar(
                    'Document deleted successfully!'
                );
            } else {
                this.snackbarService.openFailureSnackBar(
                    'Unable to delete document. Please try again.'
                );
            }
        });

    }

    /** Rebuilds `materials` — and with it every resolved kind, badge and sanitised URL. */
    private setMaterials(attachments: AdAttachment[]): void {
        this.materials = attachments.map((attachment) => {
            const kind: AdFileKind = this.marketingReviewService.fileKindOf(attachment.fileName);
            const format: string = this.marketingReviewService.formatLabelOf(attachment.fileName);

            return {
                attachment,
                kind,
                format,
                icon: this.marketingReviewService.iconOf(attachment.fileName),
                tone: this.marketingReviewService.toneOf(attachment.fileName),
                meta: [format, this.marketingReviewService.fileSizeLabel(attachment.fileSizeKb)].join(' · '),
                safeUrl: kind === 'pdf'
                    ? this.sanitizer.bypassSecurityTrustResourceUrl(attachment.url)
                    : null
            };
        });

        if (this.activeMaterial > this.materials.length - 1) {
            this.activeMaterial = Math.max(0, this.materials.length - 1);
        }
    }

    private get attachments(): AdAttachment[] {
        return this.materials.map((material) => material.attachment);
    }

    // ── Preview ─────────────────────────────────────────────────────────────────

    protected togglePreview(): void {
        this.previewOpen = !this.previewOpen;
    }

    protected selectMaterial(index: number): void {
        this.activeMaterial = index;
        this.zoom = 100;
    }

    /** The file the preview pane is rendering, `null` before anything is attached. */
    protected get preview(): Material | null {
        return this.materials[this.activeMaterial] ?? null;
    }

    /** Only the plate-rendered kinds can be zoomed; a video and a document card cannot. */
    protected get isZoomable(): boolean {
        return this.preview?.kind === 'image' || this.preview?.kind === 'pdf';
    }

    protected zoomIn(): void {
        this.zoom = Math.min(this.ZOOM_MAX, this.zoom + this.ZOOM_STEP);
    }

    protected zoomOut(): void {
        this.zoom = Math.max(this.ZOOM_MIN, this.zoom - this.ZOOM_STEP);
    }

    protected get canZoomIn(): boolean {
        return this.zoom < this.ZOOM_MAX;
    }

    protected get canZoomOut(): boolean {
        return this.zoom > this.ZOOM_MIN;
    }

    /** Canvas width in px — the same 470px plate the review page zooms. */
    protected get canvasWidth(): number {
        return Math.round(470 * (this.zoom / 100));
    }

    /** First image attachment becomes the hero creative shown in lists and on the review page. */
    private get previewImageUrl(): string {
        const image = this.materials.find((material) => material.kind === 'image');
        return image?.attachment.url ?? this.editingAd?.previewImageUrl ?? '';
    }

    // ── Save / submit ───────────────────────────────────────────────────────────

    protected async saveAsDraft(): Promise<void> {
        // A draft only needs a title, so the adviser can park an idea and come back to it.
        const titleControl = this.adFg.get('title');
        if (titleControl?.invalid) {
            titleControl.markAsTouched();
            this.snackbarService.openWarningSnackBar('Give the advertisement a title before saving it as a draft.');
            return;
        }
        // this.persist(true);
        await this.saveApplication('Draft');

    }

    protected async submitForReview(): Promise<void> {
        if (this.adFg.invalid) {
            this.adFg.markAllAsTouched();
            this.snackbarService.openWarningSnackBar('Please complete the highlighted fields before submitting.');
            return;
        }

        if (!this.materials.length) {
            this.snackbarService.openWarningSnackBar('Attach the marketing material before submitting for verification.');
            return;
        }

        // this.persist(false);
        await this.saveApplication('Submitted');
    }
    async saveApplication(status: 'Draft' | 'Submitted'): Promise<void> {

        this.setSprinner.set(true);
        let savecreateAdDetail = new CreateAdModels();
        savecreateAdDetail.setApplicantRepCode(this.currentRepCode);
        savecreateAdDetail.setApplicantBranchId(String(this.adFg.get('branch')?.value));
        savecreateAdDetail.setMarketingTitle(this.adFg.get('title')?.value?.trim());
        savecreateAdDetail.setMarketingMaterialTypeId(Number(this.adFg.get('materialType')?.value));
        savecreateAdDetail.setTargetRecepients(this.adFg.get('targetRecipients')?.value?.trim());
        savecreateAdDetail.setExpectedDateOfDissemination(this.useDisseminationNote ? '' :
            this.toIsoDate(this.adFg.get('expectedDisseminationDate')?.value));
        savecreateAdDetail.setExpectedCommentOfDissemination(this.useDisseminationNote ?
            this.adFg.get('disseminationNote')?.value?.trim() : '');
        savecreateAdDetail.setModeOfDissemination(this.adFg.get('modeOfDissemination')?.value?.trim());
        savecreateAdDetail.setDateOfApplication(moment().format('YYYY-MM-DD'));
        savecreateAdDetail.setDateIssuedByMarketing('');
        savecreateAdDetail.setIssuerRepCode('');
        savecreateAdDetail.setStatus(status.toLowerCase());
        savecreateAdDetail.setSubmittedBy(this.currentUserName);
        savecreateAdDetail.setSubmittedByRepCode(this.currentRepCode);
        savecreateAdDetail.setEdmExpectedDate('');
        savecreateAdDetail.setReferenceId(0);

        if (this.editingApplicationId !== null) {
            savecreateAdDetail.setMarketingReviewApplicationId(
                this.editingApplicationId
            );
        }
        const responseData = await this.CreateAdService
            .saveCreateAdDetails(savecreateAdDetail)
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });
        this.setSprinner.set(false);
        // console.log("Saveresponse", responseData)

        if (responseData.status === HttpStatusCode.Ok) {

            if (this.editingApplicationId === null) {
                const responseValue = responseData.data?.responsedata;

                if (responseValue?.status === 'success' && responseValue?.applicationId) {
                    this.editingApplicationId = Number(responseValue.applicationId);
                }
            }

            if (this.editingApplicationId !== null) {
                for (const file of this.pendingDocumentUploads) {
                    await this.uploaddocument(file);
                }

                this.pendingDocumentUploads = [];
                // this.snackbarService.openSuccessSnackBar(
                //     'Application saved and document uploded successfully.'
                // );
                // this.router.navigate(['/marketing-review']);

                this.persist(status === 'Draft');
            } else {
                this.persist(status === 'Draft');

            }
        }

        else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackbarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackbarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
    }


    private async persist(asDraft: boolean): Promise<void> {
        const formValue = this.adFg.getRawValue();

        const payload: SaveAdPayload = {
            ...(this.editingAd ? { id: this.editingAd.id } : {}),
            title: (formValue.title ?? '').trim(),
            // One branch is chosen; the stored shape stays a list. See `MarketingAd.branches`.
            branches: formValue.branch ? [formValue.branch as string] : [],
            targetRecipients: (formValue.targetRecipients ?? '').trim(),
            // Mutually exclusive, so the stored record never carries both.
            expectedDisseminationDate: this.useDisseminationNote
                ? ''
                : this.toIsoDate(formValue.expectedDisseminationDate),
            disseminationNote: this.useDisseminationNote ? (formValue.disseminationNote ?? '').trim() : '',
            modeOfDissemination: (formValue.modeOfDissemination ?? '').trim(),
            materialType: (formValue.materialType ?? '') as AdMaterialType | '',
            previewImageUrl: this.previewImageUrl,
            attachments: this.attachments,

            // Not collected by the design's compose form — carried over when editing a draft,
            // otherwise defaulted so the stored record stays well-formed.
            channel: this.editingAd?.channel ?? this.DEFAULT_CHANNEL,
            category: this.editingAd?.category ?? '',
            targetAudience: this.editingAd?.targetAudience ?? '',
            campaignStartDate: this.editingAd?.campaignStartDate
                ?? this.toIsoDate(this.editingAd?.createdOn ?? new Date()),
            campaignEndDate: this.editingAd?.campaignEndDate ?? '',
            description: this.editingAd?.description ?? '',
            bodyCopy: this.editingAd?.bodyCopy ?? '',
            callToAction: this.editingAd?.callToAction ?? '',
            disclaimers: this.editingAd?.disclaimers ?? ''
        };

        this.setSprinner.set(true);
        await this.marketingReviewService.saveAd(payload, asDraft)
            .then(() => {
                this.snackbarService.openSuccessSnackBar(
                    asDraft ? 'Advertisement saved as a draft.' : 'Advertisement submitted for verification.'
                );
                this.router.navigate(['/marketing-review']);
            })
            .catch((error) => {
                console.error('Failed to save the advertisement:', error);
                this.snackbarService.openFailureSnackBar('We couldn’t process your request. Please try again');
            });
        this.setSprinner.set(false);
    }

    /** Confirms before discarding, but only when there is something to lose. */
    protected cancel(): void {
        if (this.adFg.pristine && !this.materials.length) {
            this.router.navigate(['/marketing-review']);
            return;
        }

        this.matDialog
            .open(ConfirmPopupComponent, {
                width: '420px',
                data: {
                    message: 'Discard this advertisement?',
                    message1: 'Any details you have entered will be lost.',
                    hidecancel: false
                }
            })
            .afterClosed()
            .subscribe((result) => {
                if (result?.data) {
                    this.router.navigate(['/marketing-review']);
                }
            });
    }

    // ── Helpers ─────────────────────────────────────────────────────────────────

    /**
     * `yyyy-MM-dd`, so the stored value is timezone-stable for a date-only field.
     *
     * Takes a `Moment` as well as a `Date` — the datepicker's control holds whatever the moment
     * adapter put there, and formatting it locally is what keeps a date picked in the evening from
     * being stored as the day before.
     */
    private toIsoDate(value: Moment | Date | string | null | undefined): string {
        if (!value) {
            return '';
        }
        const date: Moment = moment.isMoment(value) ? value : moment(value);
        return date.isValid() ? date.format('YYYY-MM-DD') : '';
    }

    protected hasError(controlName: string): boolean {
        const control = this.adFg.get(controlName);
        return !!control && control.invalid && control.touched;
    }

    protected get currentUserName(): string {
        return this.sessionStorageService.getCurrentUserPersonName() || '—';
    }

    protected get currentRepCode(): string {
        return String(this.sessionStorageService.getCurrentRepCode() ?? '—');
    }
}